import { PDFForm, PDFPage, rgb } from 'pdf-lib'

import {
  IncidentActionType,
  IncidentHistory,
  IncidentType,
} from '@/graphql/generated/schemas'
import {
  formatDateTime,
  formatDateTimeInTimeZone,
} from '@/utils/formatDateTime'
import { getAiOrCreatorRoleName } from '@/utils/formats'

import {
  CannedIncidentActionType,
  IncidentNoteType,
  NonActionItem,
} from '../../types/feedTypes'
import {
  formatEmailRecipientCount,
  getDescriptionMessage,
} from '../../utils/feedUtils'
import { TimelineAction } from '../types/types'

const formatPriorityValue = (value: string | null | undefined): string => {
  if (!value) return 'No Priority'
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
}

const REPORT_TIMESTAMP_FORMAT = 'HH:mm:ss | MM-dd-yyyy'

export const formatReportTimestamp = (
  dateTime: string,
  timeZone?: string | null
) =>
  formatDateTimeInTimeZone(dateTime, timeZone, REPORT_TIMESTAMP_FORMAT) ||
  formatDateTime(dateTime, REPORT_TIMESTAMP_FORMAT)

export const getActionPerformed = (
  actionType: IncidentActionType | NonActionItem | CannedIncidentActionType,
  history?: IncidentHistory,
  emailRecipientCount?: number
) => {
  switch (actionType) {
    case IncidentActionType.AcknowledgementByGuard:
    case IncidentActionType.AcknowledgementByOperator:
      return 'acknowledged the incident.'

    case IncidentActionType.OnScene:
      return 'is on scene.'

    case IncidentActionType.ReleaseByOperator:
    case IncidentActionType.ReleaseByGuard:
      return 'released the incident.'

    case IncidentActionType.Cleared:
      return 'cleared the incident.'

    case IncidentActionType.Resolved:
      return 'resolved the incident.'

    case IncidentActionType.OrderDeviceRepair:
      return 'contacted technician.'

    case IncidentActionType.SetRealToFalse:
      return 'marked the incident as false.'

    case IncidentActionType.SetRealToTrue:
      return 'marked the incident as real.'

    case IncidentActionType.StartEmergencyCall:
      return 'called Emergency Number'

    case IncidentActionType.EndEmergencyCall:
      return `'s Emergency Call ended'`

    case IncidentActionType.TakeoverByGuard:
      return 'took over the incident.'

    case IncidentActionType.Flag:
      return 'has flagged the incident for further review and analysis.'

    case NonActionItem.IncidentNote:
      return 'added a note:'

    case NonActionItem.AudioMessage:
      return 'sent a voice message.'

    case CannedIncidentActionType.Email:
      return 'sent an email notification to '

    case IncidentActionType.Mute:
      return 'muted the incident.'

    case IncidentActionType.SendEmail:
      return `emailed ${formatEmailRecipientCount(emailRecipientCount)}.`

    case IncidentActionType.SystemPriorityAssignment:
      return `changed priority from No Priority to ${formatPriorityValue(
        history?.newValue
      )} based on incident category default`

    case IncidentActionType.PriorityChange: {
      const oldPriority = history?.oldValue
      const newPriority = history?.newValue
      if (oldPriority === null || oldPriority === undefined) {
        return `set priority as ${formatPriorityValue(newPriority)}.`
      }
      return `changed the priority from ${formatPriorityValue(
        oldPriority
      )} to ${formatPriorityValue(newPriority)}.`
    }

    default:
      return ''
  }
}

export const calculateResolutionTimeInDays = (
  resolvedAt: string,
  createdAt: string
): string => {
  const resolutionTimeInMs =
    new Date(resolvedAt).getTime() - new Date(createdAt).getTime()

  const seconds = Math.floor((resolutionTimeInMs / 1000) % 60)
  const minutes = Math.floor((resolutionTimeInMs / 1000 / 60) % 60)
  const hours = Math.floor((resolutionTimeInMs / 1000 / 60 / 60) % 24)

  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    seconds.toString().padStart(2, '0'),
  ].join(':')
}

export const getSplitImages = (
  imageUrls: string[],
  notes: IncidentNoteType[]
) => {
  const imagesFromNotes = notes
    .filter((note) => note.media?.length > 0)
    .map((note) => note.media)
    .flat()
    .filter(
      (media) =>
        media.mimeType === 'image/jpeg' ||
        media.mimeType === 'image/png' ||
        media.mimeType === 'image/jpg'
    )
    .map((media) => media.url)

  const incidentImages = imageUrls.concat(imagesFromNotes)

  const leftHalfImages = incidentImages.filter(
    (_: string, i: number) => i % 2 === 0
  )
  const rightHalfImages = incidentImages.filter(
    (_: string, i: number) => i % 2 !== 0
  )

  return { leftHalfImages, rightHalfImages }
}

export const getIncidentDescription = (
  type: IncidentType | null | undefined,
  description: string | null | undefined,
  floorName: string | null | undefined,
  deviceName: string | null | undefined,
  name: string | null | undefined
) => {
  const trimmedDescription = description?.trim()
  const deviceFloorDescription =
    name && deviceName && floorName
      ? `${name} was created from ${deviceName} on ${floorName}`
      : trimmedDescription || 'No description'

  switch (type) {
    case IncidentType.Tailgating:
      return getDescriptionMessage(type)
    case null:
      return trimmedDescription || 'Manual incident'
    default:
      return deviceFloorDescription
  }
}

export const getFieldAppearanceOptions = (
  page: PDFPage,
  x: number,
  y: number,
  height = 135
) => {
  // Inset the field enough that its white background never overlaps the
  // 1pt box border drawn by EditableFields, even with ~2pt layout drift.
  const fieldInset = 3

  return {
    x: x + fieldInset,
    y: y + fieldInset,
    width: page.getWidth() - x * 2 - fieldInset * 2,
    height: height - fieldInset * 2,
    borderWidth: 0,
    backgroundColor: rgb(1, 1, 1),
  }
}

export const createPDFTextField = (
  form: PDFForm,
  fieldName: string,
  text: string
) => {
  const field = form.createTextField(fieldName)
  field.setText(text)
  field.enableMultiline()
  field.enableScrolling()
  return field
}

export const isNotAssignment = (action: TimelineAction) =>
  action.__typename === NonActionItem.SystemAction &&
  !action.systemMessage.includes('assign')

export const userAction = (action: TimelineAction) => {
  if (
    action.__typename === NonActionItem.AudioMessage ||
    action.__typename === NonActionItem.IncidentNote ||
    action.__typename === 'IncidentAction' ||
    action.__typename === CannedIncidentActionType.Email
  ) {
    return action
  }
  return null
}

export const isAudioOrNote = (action: TimelineAction) =>
  action.__typename === NonActionItem.IncidentNote ||
  action.__typename === NonActionItem.AudioMessage

export const shouldDisplayDateForAction = (action: TimelineAction) =>
  (action.__typename === NonActionItem.SystemAction &&
    !action.systemMessage.includes('assign')) ||
  action.__typename !== NonActionItem.SystemAction

export const isIncidentNote = (action: TimelineAction) =>
  action.__typename === NonActionItem.IncidentNote

export const getActionUser = (action: TimelineAction) => {
  if (action.__typename === CannedIncidentActionType.Email)
    return 'AI Operator '
  if (action.__typename === 'IncidentAction' && action.isRuleAction)
    return 'Rules Engine '
  return getUserNameAndRole(action)
}

export const getUserNameAndRole = (action: TimelineAction) => {
  const creator = userAction(action)?.creator
  if (!creator) {
    return 'System '
  }
  return `${getAiOrCreatorRoleName(creator)} ${creator.firstName ?? ''} ${
    creator.lastName ?? ''
  } `
}
